/* eslint-disable max-lines -- translations file, one block per campaign section */
import { EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING } from '@Common/constants/employee_savings';
import type { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { EmployeeSavingsRiskProfile } from '@Common/types/profile/employee_savings';

import { formatCentsNumber } from '@sh4res/bo-common/utils';

export const b2bCampaignsTranslations = {
  dashboard: {
    answer_rate: {
      label: 'Taux de réponse',
      chart: {
        center_label: "Nombre d'affiliés",
      },
      legend: {
        at_least_one: 'A fait au moins un choix actif',
        inactive: 'Inactif',
      },
    },
    total_amounts: {
      label: 'Montants totaux',
      chart: {
        center_label: 'Montants totaux',
      },
      legend: {
        payed_out: 'Versé immédiatement',
        invested_per_plan_category: "Investi dans un dispositif d'épargne",
        pei: 'PEI Soles / PEE',
        percoi: 'PERECO-I Soles / PERECO',
        er: 'Contrat assureur externe',
        choice_unknown: 'Choix encore inconnu',
      },
      description: 'Répartition des montants nets de CSG et CRDS',
    },
    payment: {
      sepa_direct_debit_date: 'Date de prélèvement',
      funds_request_date: "Date d'appel de fonds",
    },
    allocations: {
      export: {
        label: 'Télécharger le rapport',
        detailed_label: 'Export détaillé',
        file_name_draft: 'Allocations_Operation_Brouillon_%{date}.xlsm',
        errors: {
          report_generation_failed: 'Erreur lors de la génération du rapport : %{error}',
        },
      },
    },
  },
  fields: {
    fiscal_period_end: 'Date de fin de la période fiscale',
    allocations_file: "Fichier d'allocations",
    products: 'Couverture Produit',
    value_sharing_bonus_fiscal_rule: 'Règle fiscale Prime de Partage de la Valeur',
    value_sharing_bonus_fiscal_rule_helper:
      'Détermine le calcul des taxes (CSG/CRDS) pour cette campagne de Prime de Partage de la Valeur',
    savings_funds_request_id: 'ID de la demande de fonds',
    total_net_amount: 'Montant total à payer',
    bank_account: 'Compte bancaire de prélèvement',
    direct_debit_date: 'Date de prélèvement',
    campaign_operation_summary: 'Compte-rendu de traitement',
    fund_request_document: 'Document de demande de fonds',
    payment_method: 'Méthode de paiement',
    group_operation_gross_amount: "Montant brut de l'opération de groupe",
    reference_year: 'Année de référence',
    pass_for_reference_year: 'PASS pour %{year} : %{amount}',
    pass_not_found_for_year: 'Aucun indice PASS trouvé pour %{year}',
    matching_contribution_enabled: 'Abondement activé',
  },
  choices: {
    type: {
      participation: 'Participation',
      profit_sharing: 'Intéressement',
      value_sharing_bonus: 'Prime de Partage de la Valeur',
      voluntary_deposit: 'Versement volontaire entreprise',
      employee_voluntary_deposit: 'Versement volontaire',
      unused_vacation_days: 'Compte épargne-temps',
      ccb_matured_election: 'Échéance légale',
      withdrawal_at_maturity: 'Rachat à échéance',
      matching_contribution: 'Abondement',
    } satisfies Record<GetB2bCampaignOneResponse['type'] | 'employee_voluntary_deposit', string>,
    reason: {
      standard: 'Standard',
      adjustment: 'Redressement',
      regularization: 'Regularisation',
      exceptional: 'Exceptionnel',
      omission: 'Omission',
    } satisfies Record<NonNullable<GetB2bCampaignOneResponse['reason']>, string>,
    payment_method: {
      sepa: 'SEPA',
      transfer: 'Virement bancaire',
    } satisfies Record<NonNullable<GetB2bCampaignOneResponse['payment_method']>, string>,
    status: {
      campaign_created: 'Créée',
      campaign_started: 'En cours',
      campaign_ended: 'Opération terminée',
      campaign_investment_amount: 'Définition des fonds investis lors de la campagne',
      company_funds_await: "En attente des fonds de l'entreprise",
      company_funds_received: "Fonds de l'entreprise reçus",
      finished: 'Complétée',
      campaign_started_failed: 'Échec début de opération',
      campaign_ended_failed: 'Échec fin de opération',
      campaign_investment_amount_failed:
        'Échec lors de la définition des fonds investis lors de la campagne',
      company_funds_await_failed: "Échec attente des fonds de l'entreprise",
      company_funds_received_failed: "Échec fonds de l'entreprise reçus",
      finished_failed: 'Échec complétée',
      campaign_draft: 'Brouillon',
      campaign_cancelled: 'Annulée',
      campaign_cancelled_failed: 'Échec de l’annulation',
      campaign_draft_failed: 'Échec du brouillon',
      campaign_published: 'Publié',
      campaign_published_failed: 'Échec de la publication',
      campaign_ending_grace_period: 'Période de grâce de fin',
      campaign_ending_grace_period_failed: 'Échec de la période de grâce de fin',
      campaign_unused_vacation_days_cash_conversion: 'Conversion des jours CET en montant',
      campaign_unused_vacation_days_cash_conversion_failed:
        'Échec de la conversion des jours CET en montant',
      campaign_pending_allocation_computation: "En attente du calcul de l'allocation",
      campaign_pending_allocation_computation_failed: "Échec du calcul de l'allocation en attente",
      campaign_awaiting_customer_confirmation: 'En attente de la confirmation du client',
      campaign_awaiting_customer_confirmation_failed:
        'Échec de la confirmation du client en attente',
      campaign_company_allocation_choices_processed: "Choix d'allocation de l'entreprise traités",
      campaign_company_allocation_choices_processed_failed:
        "Échec du traitement des choix d'allocation de l'entreprise",
    } satisfies Record<
      Exclude<GetB2bCampaignOneResponse['status'] | 'campaign_created', null>,
      string
    >,
    amount_type_input: {
      net: 'Le montant total est net de CSG/CRDS',
      gross: 'Le montant total est brut',
    },
    payment_methods: {
      sepa_linked: 'Prélèvement SEPA',
      sepa_new: 'Nouveau prélèvement SEPA',
      transfer: 'Virement bancaire',
    },
    value_sharing_bonus_fiscal_rule: {
      with_csg_crds_deduction: 'PPV avec abattement sur CSG/CRDS',
      exempt_from_csg_crds: 'PPV exonérée de CSG/CRDS',
      without_csg_crds_deduction: 'PPV sans abattement sur CSG/CRDS',
    },
  },
  sections: {
    affiliates: {
      title: 'Données des adhérents',
      description:
        'Votre contrat stipule que le calcul des attributions individuelles est effectué par le teneur de compte selon la règle ci-dessous.',
      distribution_title: 'Détails de la répartition %{type}',
      rule: 'Règle',
      distribution_uniformly: 'Uniformément',
      distribution_salary: 'Proportionnellement aux salaires perçus',
      distribution_tenure: 'Proportionnellement au temps de présence',
      distribution_total: 'Total',
      distribution_file: 'Import de fichier',
      distribution_file_url: 'Fichier de règles de distribution',
      distribution_file_desc:
        'Pour calculer les attributions, vous devez importer des données supplémentaires sur les adhérents (salaires bruts, temps de présence effectif et temps de présence contractuel).\n\nLa configuration de votre répartition ne sera réussie que si la liste des adhérents de votre entreprise est correctement complétée. En cas d’écart entre le fichier importé et la liste actuelle (adhérents supplémentaires ou données incohérentes), l’opération sera bloquée. Veuillez vérifier et compléter le répertoire avant de procéder à l’import.',
    },
    general: {
      title: 'Information générale',
      details: 'Détails',
      operation_processing: 'Traitement de l’opération',
      processing_options_contract_note:
        'Ces valeurs sont pré-remplies à partir de la configuration actuelle du contrat.',
      operation_amount: "Montant de l'opération",
      operation_amount_description:
        "Ceci est l'enveloppe totale que vous avez calculée pour votre opération.",
      group_operation_gross_amount: "Montant brut de l'opération de groupe",
      group_operation_gross_amount_description:
        "Ceci est l'enveloppe totale brute que vous avez calculée pour votre opération de groupe.",
      amount_type_input: 'Spécifiez si ce montant est brut ou net de CSG/CRDS* :',
      reference_fiscal_period: 'Période fiscale de référence',
      fiscal_period_past: 'La période fiscale de référence ne peut pas être dans le futur',
      select_company_first: 'Veuillez sélectionner une entreprise en premier',
      no_matching_contribution_config_for_year:
        "Aucun des plans concernés n'a de configuration d'abondement pour l'année sélectionnée.",
      processing: 'Traitement des opérations',
      errors: {
        invalid_contracts:
          "Lors de la sélection de plusieurs contrats, tous doivent avoir les mêmes paramètres de traitement de l'opération",
        invalid_contracts_terminated:
          'Les contrats résiliés ne peuvent pas être utilisés pour une opération collective',
        invalid_contracts_voluntary_deposit:
          "Lors de la sélection de plusieurs contrats, tous doivent avoir les mêmes conditions valides pour une opération de dépôts volontaires de l'entreprise",
        invalid_contracts_voluntary_deposit_removed: `Les contrats qui n'ont pas les conditions pour une opération de dépôts volontaires de l'entreprise ont été enlevés des contrats sélectionnés`,
        invalid_contracts_cet_allocation_computed_by:
          'Les opérations CET ne peuvent pas avoir le calcul des quotes-parts par le teneur de compte. Mettez à jour les paramètres de traitement du contrat.',
        no_common_cash_sources:
          "Il n'y a pas de source de versement en commun entre les plans des contrats sélectionnés",
        different_agreement_distributions:
          'Les règles de répartition ne sont pas les mêmes sur tous les contrats sélectionnés',
      },
    },
    review: {
      gross_amount: 'Montant Brut',
      net_amount: 'Montant net de la CSG/CRDS',
      net_amount_deposit: 'Montant net du versement',
      title: 'Revoir',
      errors: {
        no_allocations: 'Aucune allocation définie',
        no_contract_plans: "Il n'y a aucun plan d'épargne activé pour les contrats sélectionnés",
        different_processing_options:
          'Les paramètres de traitement ne sont pas les mêmes sur tous les contrats sélectionnés',
        invalid_employee_allocations: 'Les allocations de certains employés ne sont pas valides',
        zero_amount_allocations:
          'Certains employés ont un montant à 0 € (total ou sur un type de jour) : renseignez un montant pour chacun',
        day_types_mismatch:
          "Les types de jours de certains employés dans le fichier diffèrent de ceux qu'ils ont investis : corrigez le fichier et importez-le à nouveau",
        missing_employees_link: 'Vérifiez le compte des employés ici',
        employee_not_found: 'Adhérent non éligible',
        employee_not_fully_onboarded:
          "Cet employé n'est pas totalement intégrés dans l'application.",
        employee_not_exists: "Le compte de cet employé n'existe pas avec le NIR donné.",
        total_not_match: 'Les allocations ne correspondent pas au total attendu',
        total_not_match_mc: "L'abondement ne correspond pas au total attendu",
        some_choice_at_zero:
          "Veuillez vérifier les choix d'allocations, un ou plusieurs d'entre eux ont une valeur de 0 euros.",
        some_choice_at_zero_mc:
          "Veuillez vérifier les choix d'abondement, un ou plusieurs d'entre eux ont une valeur de 0 euros.",
        matching_contribution_ceiling_exceeded:
          "L'abondement dépasse le plafond restant pour les salariés suivants",
        matching_contribution_ceiling_exceeded_by: 'dépassement de %{amount} €',
        warning_zero_amount: 'Contient une valeur de 0 euros',
        user_closed: 'Le compte de cet utilisateur est clôturé',
        duplicate_employee: 'Le même adhérent apparaît plusieurs fois dans les allocations',
      },
      warnings: {
        already_answered: 'A déjà fait son choix d’investissement',
        campaigns_blocked: 'Restriction réglementaire (paiement uniquement)',
        campaigns_blocked_restriction: 'Campagnes bloquées (paiement uniquement)',
        campaigns_blocked_employees: {
          part1:
            'Depuis le 12 avril 2022, les ressortissants russes ou biélorusses ne peuvent plus investir dans des supports de placement libellés en Euros (Règlement UE 833/2014 consolidé concernant des mesures restrictives eu égard aux actions de la Russie déstabilisant la situation en Ukraine). Cette mesure restrictive ne s’applique pas aux ressortissants d’un Etat membre de l’UE ou aux titulaires d’un titre de séjour en cours de validité dans un Etat membre.',
          part2:
            'Les salariés ci-dessous sont nés en Russie ou Biélorussie et sont donc présumés avoir cette nationalité.',
          part3: 'Afin de leur permettre d’effectuer leur choix d’investissement, ',
          part3bold:
            'merci de contacter le Service client afin de fournir une preuve d’exemption :',
          bullet1:
            'Une pièce d’identité prouvant une nationalité (ou double nationalité) d’un pays de l’Union européenne',
          bullet2:
            'Dans le cas où ils n’auraient que la nationalité russe, un titre de séjour en cours de validité dans un pays de l’Union européenne',
          part4: 'A défaut, seul le paiement immédiat de leur quote-part pourra être proposé.',
        },
        company_vp_too_large: `Allocations de versement volontaire de l'entreprise > ${formatCentsNumber(EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING)}`,
        title: 'Certains salariés nécessitent des vérifications supplémentaires',
      },
    },
    confirmation: {
      title: 'Confirmation',
      summary: 'Résumé et confirmation',
      operation_type: "Type d'opération",
      reference_fiscal_period: 'Période fiscale de référence',
      reference_fiscal_period_value: 'De %{from} à %{to}',
      reference_year: 'Année de référence',
      gross_amount: 'Montant brut total',
      net_amount: 'Montant total net de CSG/CRDS',
      net_amount_deposit: 'Montant total des versements',
      total_amount_to_be_paid: 'Montant total à payer',
      total_amount_to_be_value: 'sera calculé à la fin de la période de consultation',
      matching_contributions_next_fund_request:
        'Le montant sera ajouté dans votre prochain appel de fond des contributions d’abondement',
      matching_contributions_to_be_paid: 'Montant total des contributions d’abondement à payer',
      matching_contributions_zero:
        'Le montant total des contributions d’abondement est de 0, cette campagne ne peut donc pas être enregistrée. Veuillez vérifier que l’abondement est activé sur les supports sélectionnés.',
      matching_contributions_to_be_value: 'sera calculé à la fin de la période de consultation',
      consultation_period: 'Période de consultation',
      consultation_period_value: 'De %{from} à %{to}',
      funds_request_date: "Date d'appel de fonds",
      funds_request_date_value: '%{date} (%{hours}h après la fin de la période de consultation)',
      funds_request_date_value_at: 'à',
      employees_concerned: "Nombre d'employés concernés",
      payment_method_title: 'Mode de paiement',
      sepa_direct_debit_date: 'Date de prélèvement',
      sepa_direct_debit: 'Prélèvement SEPA',
      sepa_warning:
        'Vous avez opté pour le prélèvement mais aucun mandat SEPA n’est encore associé à votre compte. Merci de vous rendre dans l’onglet Informations de paiement pour en ajouter un et permettre le règlement de votre opération.',
      bank_transfer: 'Virement bancaire',
      late_payment_interests: 'Montant des intérêts de retard',
      confirmations_title: 'Confirmations',
      confirm_accuracy: "Je confirme l'exactitude des données",
      confirm_sepa_debit:
        "Je confirme que j'accepte le prélèvement des montants à investir à la fin de la période de consultation",
      confirm_sepa_debit_company_allocator:
        "Je confirme que j'accepte le prélèvement de %{total_amount_to_be_paid}",
      choice_collected_by: 'Collecte des choix par',
      payout_by: 'Règlements immédiats par',
      allocation_computed_by: 'Calcul des quotes-parts par',
      matching_contribution_computed_by: 'Abondement calculé par',
    },
    success_modal: {
      title: 'Opération créée avec succès',
      body: `Félicitations ! Votre opération a été créée avec succès. Vous pouvez la suivre via votre tableau de bord des opérations.

    Un email récapitulatif vous a également été envoyé.`,
      btn: 'Compris',
    },
    success_modal_updated: {
      title: 'Opération mise à jour avec succès',
      body: 'Votre opération a été mise à jour avec succès. Vous pouvez la suivre via votre tableau de bord des opérations.',
    },
    success_modal_unused_vacation_days: {
      body: 'Pour la prochaine etape, vous pourrez charger les allocations apres la periode de consolidation, le %{date}.',
    },
    success_modal_allocations_to_compute: {
      title: 'Opération préparée avec succès',
      body: `Votre opération a été préparée avec succès.
Nous allons maintenant calculer l’allocation pour vous. Une fois terminé, vous pourrez la vérifier avant de la lancer.`,
    },
    success_modal_allocations_computed: {
      title: 'Opération préparée avec succès',
      body: `L’opération a été préparée avec succès.
Elle est maintenant prête pour la révision finale par l’agent, qui pourra l’approuver et lancer la campagne.`,
    },
    duration_warning_modal: {
      title: 'Avertissement',
      body: 'Vous êtes sur le point de créer une opération dont la période de consultation est inférieure à 15 jours. Voulez-vous continuer malgré tout ?',
    },
    company_collected_choices_blocking: {
      title:
        'Attribution effectuée à différents profils au sein des mêmes plans pour les affiliés suivants',
      body: 'Vous ne pouvez pas attribuer des montants à différents profils d’un même plan pour un même affilié. Veuillez corriger les erreurs dans le fichier, puis revenir pour finaliser la création de l’opération.',
      affiliates: 'Affiliés concernés:',
    },
    removed_employees: {
      title: 'Des adhérents vont être retirés de l’opération',
      body: 'Les adhérents ci-dessous ont un montant de 0 et seront retirés de l’opération. Ils ne pourront plus rien allouer, et tout choix d’investissement déjà effectué sera annulé. Souhaitez-vous confirmer ?',
      affiliates: 'Adhérents impactés :',
    },
    company_collected_choices: {
      title:
        'Attribution effectuée à des profils différents de ceux actuellement associés aux affiliés',
      body: 'Attention les profils des salariés ci-dessous sont différents des profils sur lesquels vous allez investir, un arbitrage de leur plan sera déclenché automatiquement pour s’aligner sur le dernier profil. Souhaitez-vous confirmer?',
      affiliates: 'Affiliés concernés:',
    },
    allocations: {
      placement_support: 'Support de placement',
      matching_contribution_amount: 'Montant de l’abondement',
      risk_profile_current: 'Profil actuel',
      risk_profile: {
        balanced: 'Équilibré',
        careful: 'Prudent',
        dynamic: 'Dynamique',
        sicav: 'Retraite SICAV',
      } satisfies Record<EmployeeSavingsRiskProfile, string>,
      plan: 'Plan',
      identifier: 'Identifiant',
      add_row: 'Ajouter une ligne',
      payouts: 'Versements',
      ccb: 'CCB',
      title: 'Allocations',
      consultation_period: 'Période de consultation',
      errors: {
        invalid_file_version:
          'La version du fichier est invalide, la version actuelle est v%{version}.',
        invalid_plan: "Le produit %{plan} n'existe pas sur les contrats choisis.",
        ambiguous_plan:
          'Le produit %{plan} correspond à plusieurs produits sur les contrats choisis. Téléchargez à nouveau le modèle de fichier et utilisez ses noms de produits, qui contiennent leur identifiant.',
        invalid_instrument:
          "Le support de placement %{instrument} n'est pas actif sur le produit %{plan} pour une campagne %{campaign}.",
        amount_column_not_found:
          "La colonne du montant n'a pas été trouvée dans le fichier, elle doit contenir au minimum un montant.",
        mc_column_not_found:
          "La colonne de l'abondement n'a pas été trouvée dans le fichier, elle doit contenir au minimum un montant.",
        parsing_file: 'Une erreur inconnue est survenue lors du traitement du fichier.',
        plan_not_exists: "Le produit %{plan} n'existe pas.",
        invalid_field: 'Valeur invalide pour le champ %{field} à la ligne %{line}.',
        invalid_field_no_line: 'Valeur invalide pour le champ %{field}.',
        negative_amount:
          'Montant négatif (%{amount} €) pour le champ %{field} à la ligne %{line}. Les montants des allocations doivent être positifs ou nuls.',
        duplicate_employee:
          'Le même adhérent (%{identifier}) apparaît plusieurs fois aux lignes %{firstLine} et %{line}.',
        missing_day_type:
          'La colonne « Type de jour » est obligatoire : aucun type de jour à la ligne %{line}.',
        ceiling_reached: 'Plafond atteint',
        missing_consultation_period:
          "Une période de consultation (date de début et de fin) est obligatoire lorsque les choix sont collectés via l'application.",
      },
      warnings: {
        name_mismatch:
          'Les noms suivants dans le fichier ne correspondent pas aux enregistrements des employés (fichier → enregistrement) :',
      },
      consultation_period_description:
        'C’est la période durant laquelle les adhérents peuvent allouer leur prime comme ils le souhaitent. Légalement, elle doit durer au moins 15 jours. À la fin de cette période de consultation, nous vous inviterons à payer le montant global correspondant à l’opération.',

      employee_allocations: 'Allocations des employés via téléchargement de fichier',
      employee_allocations_description: {
        part1: 'La création de votre opération ne pourra aboutir que si le ',
        part2bold: 'référentiel adhérents de votre entreprise est correctement renseigné',
        part3:
          '. En cas d’écart entre le fichier importé et le référentiel adhérent (adhérents additionnels), l’opération sera ',
        part4bold: 'bloquée',
        part5:
          ". Merci de vérifier et compléter le référentiel adhérent avant d'effectuer l’import.",
      },
      download_sample_empty: 'Télécharger un fichier exemple ici - vide',
      download_sample_current_employees: 'Télécharger un fichier exemple ici - employés actuels',
      download_sample_all_employees: 'Télécharger un fichier exemple ici - tous les employés',
      download_sample_all_employees_file: 'Export exemple de tous les affiliés.xlsm',
      employee_allocations_manual: "Ou allocation manuelle des employés à l'étape suivante",
      allocations_removal_warning:
        'Tout adhérent absent d’un nouveau fichier est retiré de l’opération. S’il avait déjà effectué un choix d’investissement, celui-ci est annulé.',
      consultation_period_3_month: 'La période de consultation ne peut pas dépasser 3 mois',
      end_date_after_today:
        'La date de fin de la période de consultation doit être au minimum aujourd’hui',
      late_payment_interests: {
        title: 'Intérêts de retard',
        subtitle: 'Intérêts de retard = montant dû x TMO x Jours de retard/365',
        description:
          'La date de paiement de votre opération devrait intervenir %{days} jour(s) après la date limite légale. Par conséquent, votre opération est soumise à des intérêts de retard.',
        formula: '(montant_intérêts_de_retard) € (= {montant} x {TMO} x X/365 =)',
        question: "Souhaitez-vous inclure ces frais dans l'opération ?",
        distribution:
          'Si vous choisissez "Oui", les intérêts de retard seront répartis entre les adhérents selon une règle de pro-rata basée sur le montant brut des allocations. Si vous choisissez "Non", vous pouvez entrer le montant de vos intérêts de retard ci-dessous ou le laisser à 0 € pour ignorer les intérêts de retard.',
        choices: {
          yes: 'Oui',
          no: 'Non',
        },
        override_amount: 'Montant global des intérêts de retard auto-calculé (optionnel)',
      },
    },
    vacation_days_allocations: {
      download_title: 'Télécharger les allocations réalisées en jours de congés',
      download_description:
        "Téléchargez les allocations réalisées avec le nombre de jours de congés restants, mettez-les à jour avec les montants correspondants et re-importez le fichier contenant les montants d'investissement",
      download_link: 'Télécharger les allocations',
      upload_title: "Importer les allocations contenant les montants d'investissement",
      upload_description_part1:
        "Ajoutez un fichier contenant la liste de tous les bénéficiaires avec leurs investissements incluant tous les produits d'épargne et les montants",
      upload_description_part2:
        "La création de votre opération ne pourra aboutir que si le référentiel adhérents de votre entreprise est correctement renseigné. En cas d'écart entre le fichier importé et le référentiel adhérent (adhérents additionnels), l'opération sera bloquée. Merci de vérifier et compléter le référentiel adhérent avant d'effectuer l'import.",
    },
    vacation_days_confirmation: {
      day_type: 'Type de jour',
      day_types: {
        cet: 'Divers',
        rtt: 'RTT',
        overtime: 'Heures supplémentaires',
        paid_leave: 'Congés payés',
        seniority_leave: "Congés d'ancienneté",
        untaken_rest: 'Jours de repos non pris',
      },
      invested_amount_in_days: 'Jours investis',
      total_net_amount: 'Montant net total',
      net_amount_column: 'Net de CSG/CRDS CET',
    },
    recipients: {
      title: 'Bénéficiaires',
      description:
        'Ajoutez un fichier contenant la liste de tous les bénéficiaires avec leur nombre total de jours de congés restants',
      affiliates_warning:
        "La création de votre opération ne pourra aboutir que si le référentiel adhérents de votre entreprise est correctement renseigné. En cas d'écart entre le fichier importé et le référentiel adhérent (adhérents additionnels), l'opération sera bloquée. Merci de vérifier et compléter le référentiel adhérent avant d'effectuer l'import.",
      download_sample:
        'Télécharger un fichier exemple ici - Employés avec jours de congés restants',
      download_sample_file: 'Employés avec jours de congés restants.xlsm',
    },
    sfr: {
      title: 'Demande de fonds',
    },
    secondary_sfr: {
      title: 'Demande de fonds supplémentaire',
    },
    payment: {
      title: 'Paiement',
      can_not_pay_description:
        "Votre entreprise ne peut pas payer pour l'opération maintenant. Le paiement sera disponible à la fin de la période de consultation.",
      payment_method: 'Mode de paiement',
      upload: 'Télécharger un RIB',
      can_pay_description: "Votre entreprise peut payer l'opération maintenant.",
      edit_locked: 'Le mode de paiement ne peut plus être modifié : %{reason}.',
      cancels_fund_request_warning:
        'Changer le mode de paiement annulera la demande de fonds %{fundRequestId} et en créera une nouvelle, avec un nouveau bordereau de versement.',
    },
  },
  tabs: {
    dashboard: {
      label: 'Dashboard',
    },
    details: {
      label: 'Details',
    },
    documents: {
      label: 'Documents',
      empty: "Cette opération n'a aucun document pour le moment",
      fields: {
        affiliates_data: 'Données des adhérents',
        allocations: "Fichier d'allocations",
      },
    },
    payouts: {
      label: 'Règlements immédiats',
    },
    draft: {
      label: 'Brouillons',
    },
    in_progress: {
      label: 'En cours',
    },
    history: {
      label: 'Historique',
    },
    migrated: {
      label: 'Opérations migrées',
    },
  },
  migrated: {
    sections: {
      details: 'Détails',
    },
    fields: {
      id: 'ID Opération',
      b2b_company_id: 'ID Entreprise',
      company_name: "Nom de l'entreprise",
      status: 'Statut',
      type: 'Nature',
      subtype: 'Type',
      created_at: 'Créé le',
      fiscal_period_start: 'Date de début de la période fiscale',
      fiscal_period_end: 'Date de fin de la période fiscale',
      reference_period: 'Période de référence',
      operation_date: 'Date',
      processing_start_date: 'Date de début de la consultation',
      processing_end_date: 'Date de fin de la consultation',
      amount_brut: 'Montant total brut à payer',
      amount_net: 'Montant total net à payer',
      source_instrument: 'Instrument source',
      target_instrument: 'Instrument cible',
      source_plan_piloted: 'Plan source (piloté)',
      source_plan_libre: 'Plan source (libre)',
      target_plan_piloted: 'Plan cible (piloté)',
      target_plan_libre: 'Plan cible (libre)',
      plan_types: 'Types de produit',
    },
    choices: {
      status: {
        completed: 'Complétée',
      },
      subtype: {
        AUGCAP: 'Augmentation de capital',
        LIBRE: 'Arbitrage libre',
        REALLOCATION: 'Arbitrage reallocation',
        ECHLEG: 'Échéance légale',
        CLIENT: 'Retrait',
        ECRET: 'Écrêtement',
        ABD: 'Abondement',
        INT: 'Intéressement',
        PPV: 'Prime de Partage de la Valeur',
        RSP: 'Participation',
        VVENT: "Dépôts volontaires de l'entreprise",
        VVIND: "Dépôt volontaire de l'employé",
        VVP: 'Dépôts volontaires récurrents',
        GAG_CASH: 'Importé',
        GAG_TITR: 'Importé',
        PER_CASH: 'Transfer sortant (cash)',
        PER_TITR: 'Transfer sortant (unités)',
        CAPIT: 'Dividende',
        DISTRIB: 'Dividende',
        FUSFONDS: 'Fusion',
        INT_CCB: 'Echéance légale',
        IR: 'Intérêts de retard',
        NPAI: 'Retrait',
        REAJUS: 'Dividende',
        RGL_TITRE: 'Rachat',
        SANS_STRESS: 'Arbitrage',
      },
    },
  },
  actions: {
    create: {
      label: 'Créer une opération',
      success: 'Opération créée',
      failure: 'Erreur lors de la création de opération: %{error}',
      csv_field_label: 'Fichier CSV des allocations adhérents',
    },
    edit: {
      label: "Modifier l'opération",
      success: 'Opération mise à jour',
      failure: "Erreur lors de la mise à jour de l'opération : %{error}",
    },
    resume: {
      label: 'Reprendre',
      dialog_title: "Reprendre l'opération sans le distributeur",
    },
    delete: {
      confirm_title: "Confirmer l'annulation",
      confirm_content:
        'Êtes-vous sûr de vouloir annuler cette opération ? Cette action est irréversible.',
      confirm_cancel_ended_campaign:
        "Cette campagne est terminée. L'annuler revendra les positions déjà investies via des ordres de correction, en remboursant le montant initialement investi à la VL d'origine — toute différence de VL est absorbée par AXA. Cette action est irréversible.",
      label: 'Annuler',
      success: 'Opération marquée pour annulation',
      failure: "Échec de l'annulation de l'opération : %{error}",
    },
    fetch_mc_remaining_amounts: {
      failure: 'Échec de la récupération des montants restants MC',
    },
  },
  allocations: {
    label: 'Répartition par salariés ayant-droits',
  },
  employees_awaiting_response: {
    label: "Les employés qui n'ont pas encore fait de choix",
  },
  timeline: {
    label: {
      consultation_period: 'Période de consultation',
      payment_period: 'Période de paiement',
      reminder_date: 'Relance',
    },
  },
};
